/**
 * Hover step trail (TT-51, approved design): "ID ···· Details ···· Income"
 * with dotted connectors — green completed, red failed, gray pending.
 * Purely presentational; the loaders decide which steps appear and their
 * statuses (DTO: MemberStepPill[]). No per-member fetching — the trail data
 * rides the batched rows.
 *
 * THE shared trail (moved out of dashboards/v2 on 2026-07-13): dashboard
 * member chips, pipeline cards AND the combined report's overview tiles all
 * render through this one component with card-kit's buildStepPills — never
 * fork a per-surface variant (same rule as ScoreBandBar).
 */
import type {
	MemberStepPill,
	StepPillStatus,
} from '#app/utils/landlord-dashboard-v2/card-kit'
import { cn } from '#app/utils/misc'

/** Tooltip chrome shared by every surface that hovers a member into the
 * trail (dashboard chips, All Applicants sheet, combined overview tiles). */
export const MEMBER_TOOLTIP_CLASS =
	'rounded-2xl border border-[#F0F0F0] bg-white p-4 shadow-[0_12px_40px_rgba(0,0,0,0.12)]'

const PILL_CLASS: Record<StepPillStatus, string> = {
	completed: 'bg-[#16A3540D] text-[#16A354]',
	failed: 'bg-[#FDF4F5] text-[#DC2634]',
	pending: 'bg-[#F4F4F2] text-[#747474]',
}

const CONNECTOR_CLASS: Record<StepPillStatus, string> = {
	completed: 'border-[#16A354]',
	failed: 'border-[#DC2634]',
	pending: 'border-[#CCCCCC]',
}

export function StepPillTrail({
	steps,
	size = 'md',
}: {
	steps: MemberStepPill[]
	/** 'sm' = the pipeline cards' compact variant — same trail, same dotted
	 * connectors, smaller pills (never fork this component per surface). */
	size?: 'md' | 'sm'
}) {
	return (
		<div className="flex flex-wrap items-center gap-y-1">
			{steps.map((step, index) => (
				<span key={step.key} className="flex items-center">
					{index > 0 ? (
						// Connector takes the color of the step it leads INTO (design:
						// the dashes before "Income" turn red with a failed Income).
						<span
							aria-hidden="true"
							className={cn(
								'border-t-2 border-dotted',
								size === 'sm' ? 'mx-0.5 w-2.5' : 'mx-1 w-4',
								CONNECTOR_CLASS[step.status],
							)}
						/>
					) : null}
					<span
						className={cn(
							'rounded-full font-semibold',
							size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
							PILL_CLASS[step.status],
						)}
					>
						{step.label}
					</span>
				</span>
			))}
		</div>
	)
}
